import React, { useState, useEffect } from 'react';
import { DollarSign, Users, User, RefreshCw, AlertCircle, AlertTriangle } from 'lucide-react';
import { commissionCacheService, type CachedCommissionEntry } from '../../lib/services/commissionCacheService';
import { supabase } from '../../lib/supabase';

interface DealCommissionDisplayProps {
  dealId: string;
}

// Tipo per la discrepanza rilevata
interface DiscrepancyInfo {
  crmTotal: number;
  invoiceNet: number;
  difference: number;
}

export function DealCommissionDisplay({ dealId }: DealCommissionDisplayProps) {
  const [commissions, setCommissions] = useState<CachedCommissionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discrepancy, setDiscrepancy] = useState<DiscrepancyInfo | null>(null);

  useEffect(() => {
    loadCommissions();
  }, [dealId]);

  const loadCommissions = async () => {
    console.log('🔄 [DealCommissionDisplay] loadCommissions called for deal:', dealId);
    setLoading(true);
    setError(null);
    setDiscrepancy(null);

    try {
      // Get current user's organization
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('User not authenticated');
      }

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) {
        throw new Error('User not associated with an organization');
      }

      // Get deal info to determine the month
      const { data: deal } = await supabase
        .from('deals')
        .select('closed_at, created_at, status, title, client_id, amount, assigned_to')
        .eq('id', dealId)
        .single();

      if (!deal) {
        throw new Error('Deal not found');
      }

      // Use closed_at if available, otherwise created_at
      const referenceDate = new Date(deal.closed_at || deal.created_at);

      // Forzare ricalcolo della cache (come fa /commissions)
      // Questo garantisce che i valori siano sempre aggiornati
      console.log('🔄 [DealCommissionDisplay] Triggering cache recalculation...');
      await commissionCacheService.triggerCalculation(
        userProfile.organization_id,
        referenceDate,
        userData.user.id,
        true // forceRecalculation = true
      );

      // Delay breve per assicurarsi che il DB sia aggiornato
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get cached commissions (ora aggiornate)
      const cachedCommissions = await commissionCacheService.getCachedCommissions(
        userProfile.organization_id,
        referenceDate
      );

      // Filter for this specific deal
      let dealCommissions = cachedCommissions.filter(
        (c) => c.deal_id === dealId || c.original_deal_id === dealId
      );

      // FIX 2026-03-20: Filtra le commissioni di tipo 'agent' mostrando solo quelle
      // dell'agente attualmente assegnato al deal. Le commissioni di vecchi agenti
      // vengono preservate nel DB per storico (se hanno pagamenti) ma non devono
      // essere mostrate nella scheda dell'offerta.
      if (deal.assigned_to) {
        dealCommissions = dealCommissions.filter(c => {
          // Per i referrer e altri tipi, mantieni sempre (potrebbero essere storici validi)
          if (c.person_type !== 'agent') return true;
          // Per gli agenti, mostra solo quello attualmente assegnato
          return c.person_id === deal.assigned_to;
        });
      }

      // FIX: Deduplica per chiave logica completa, mantenendo l'entry più recente
      // Un'offerta può avere più servizi/variabili, quindi la chiave deve includere tutti i campi
      // NON preserviamo dati di pagamento da entry vecchie - potrebbero essere corrotti
      // FIX 2026-03-13: Usa deal_service_id (se disponibile) invece di service_id per distinguere
      // servizi duplicati (stesso service_id ma istanze diverse nell'offerta)
      const entryMap = new Map<string, typeof dealCommissions[0]>();
      for (const entry of dealCommissions) {
        // Usa deal_service_id se disponibile, altrimenti fallback a service_id per retrocompatibilità
        const serviceKey = entry.deal_service_id ?? entry.service_id ?? 'null';
        const key = `${entry.deal_id}_${serviceKey}_${entry.variable_id ?? 'null'}_${entry.person_id}_${entry.person_type}_${entry.month_index ?? 'null'}`;
        const existing = entryMap.get(key);
        if (!existing || new Date(entry.updated_at || 0) > new Date(existing.updated_at || 0)) {
          entryMap.set(key, entry);
        }
      }
      const uniqueDealCommissions = Array.from(entryMap.values());

      if (uniqueDealCommissions.length !== dealCommissions.length) {
        console.warn(`[DealCommissionDisplay] Removed ${dealCommissions.length - uniqueDealCommissions.length} duplicate entries`);
      }

      console.log('✅ [DealCommissionDisplay] Fresh commissions:', uniqueDealCommissions.length);
      setCommissions(uniqueDealCommissions);

      // FIX 2026-03-20: Verifica discrepanza tra totale CRM e netto fatture FiC
      // Questo aiuta l'utente a identificare variabili (es. bollo) salvate con importi errati
      try {
        // Carica fatture FiC per questo deal
        const { data: invoices } = await supabase
          .from('fattureincloud_invoices')
          .select('net_amount, amount')
          .eq('deal_id', dealId)
          .not('status', 'in', '("cancelled","deleted")');

        // Carica variabili del deal
        const { data: dealServices } = await supabase
          .from('deal_services')
          .select(`
            id,
            deal_variables (
              id,
              amount,
              type,
              base_amount,
              is_confirmed,
              is_vat_exempt,
              is_stamp_duty,
              is_stamp_duty_art15
            )
          `)
          .eq('deal_id', dealId);

        if (invoices && invoices.length > 0 && dealServices) {
          // Calcola totale netto fatture FiC
          const invoiceNetTotal = invoices.reduce((sum, inv) => sum + (Number(inv.net_amount) || 0), 0);

          // Calcola totale variabili esenti (bollo, esenti IVA) dal CRM
          let exemptVariablesTotal = 0;
          for (const ds of dealServices) {
            const variables = ds.deal_variables || [];
            for (const v of variables) {
              if (v.is_confirmed && (v.is_vat_exempt || v.is_stamp_duty || v.is_stamp_duty_art15)) {
                const varAmount = v.type === 'percentage' && v.base_amount
                  ? (Number(v.base_amount) * Number(v.amount)) / 100
                  : Number(v.amount);
                exemptVariablesTotal += varAmount;
              }
            }
          }

          // Il "netto per provvigioni" calcolato dall'RPC è: invoiceNetTotal - exemptVariablesTotal
          // Se questo non è un numero "pulito", potrebbe esserci una discrepanza
          const netForCommissions = invoiceNetTotal - exemptVariablesTotal;

          // Verifica se il risultato ha centesimi "strani" (non .00, .25, .50, .75)
          // Questo indica probabile discrepanza tra variabile salvata e valore reale
          const cents = Math.round((netForCommissions % 1) * 100);
          const isCleanAmount = cents === 0 || cents === 25 || cents === 50 || cents === 75;

          // Se il netto provvigioni non è un importo "pulito" E abbiamo variabili esenti,
          // probabilmente c'è una discrepanza nei dati
          if (!isCleanAmount && exemptVariablesTotal > 0) {
            // Calcola quale dovrebbe essere il valore "corretto" delle variabili esenti
            // basandosi sul netto fattura e arrotondando al centesimo più vicino
            const expectedNet = Math.round(netForCommissions);
            const expectedExempt = invoiceNetTotal - expectedNet;

            if (Math.abs(exemptVariablesTotal - expectedExempt) > 0.01) {
              setDiscrepancy({
                crmTotal: netForCommissions,
                invoiceNet: expectedNet,
                difference: Math.abs(netForCommissions - expectedNet)
              });
              console.warn('⚠️ [DealCommissionDisplay] Discrepanza rilevata:', {
                nettoFattura: invoiceNetTotal,
                variabiliEsenti: exemptVariablesTotal,
                nettoProvvigioni: netForCommissions,
                nettoAtteso: expectedNet,
                differenza: Math.abs(netForCommissions - expectedNet)
              });
            }
          }
        }
      } catch (discErr) {
        // Non bloccare il caricamento delle commissioni se il check discrepanza fallisce
        console.warn('⚠️ [DealCommissionDisplay] Errore nel check discrepanza:', discErr);
      }
    } catch (err) {
      console.error('Error loading commissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-4 px-4">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-5 w-5 text-gray-400 dark:text-gray-500 animate-spin" />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Caricamento provvigioni...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 px-4">
        <div className="flex items-start gap-2 p-3 bg-yellow-50/50 dark:bg-yellow-900/10 backdrop-blur-sm rounded-lg">
          <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-yellow-800 dark:text-yellow-200">
            <p className="font-medium mb-1">Impossibile caricare le provvigioni</p>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (commissions.length === 0) {
    return (
      <div className="space-y-4 px-4">
        <p className="text-xs text-gray-600 dark:text-black">
          Provvigioni previste per questa offerta
        </p>
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
          <p>Nessuna provvigione trovata per questa offerta.</p>
          <p className="text-xs mt-2">
            Le provvigioni verranno calcolate quando l'offerta verrà accettata.
          </p>
        </div>
      </div>
    );
  }

  // Group commissions by person
  const groupedCommissions = commissions.reduce((acc, commission) => {
    const key = commission.person_id;
    if (!acc[key]) {
      acc[key] = {
        person_id: commission.person_id,
        person_name: commission.person_name || 'Sconosciuto',
        person_type: commission.person_type,
        entries: [],
        total: 0,
      };
    }
    acc[key].entries.push(commission);
    acc[key].total += commission.commission_amount;
    return acc;
  }, {} as Record<string, { person_id: string; person_name: string; person_type: string; entries: CachedCommissionEntry[]; total: number }>);

  const groups = Object.values(groupedCommissions);

  return (
    <div className="space-y-3 md:space-y-4 px-3 md:px-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Provvigioni previste per questa offerta
        </p>
        <button
          onClick={loadCommissions}
          className="text-xs px-2 py-1 bg-white/50 dark:bg-gray-800/50 hover:bg-white/70 dark:hover:bg-gray-800/70 backdrop-blur-sm rounded-lg flex items-center gap-1 text-gray-700 dark:text-gray-300 transition-colors min-h-[36px] md:min-h-0"
          title="Aggiorna provvigioni"
        >
          <RefreshCw className="h-3 w-3" />
          <span className="hidden md:inline">Aggiorna</span>
        </button>
      </div>

      {/* Warning discrepanza variabili */}
      {discrepancy && (
        <div className="bg-yellow-50/80 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-2.5 md:p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-1">Discrepanza rilevata nei dati</p>
              <p>
                Il netto per provvigioni ({formatCurrency(discrepancy.crmTotal)}) non corrisponde all'importo atteso ({formatCurrency(discrepancy.invoiceNet)}).
              </p>
              <p className="mt-1 text-yellow-700 dark:text-yellow-300">
                Verifica che le variabili (es. marca da bollo, costi anticipati) siano corrette.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {groups.map((group) => (
          <div key={group.person_id} className="rounded-lg overflow-hidden bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm">
            {/* Group Header */}
            <div className="bg-gray-50/80 dark:bg-gray-900/50 px-3 py-2 md:py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium ${
                  group.person_type === 'agent' ? 'bg-[#0f6cbd]' : 'bg-green-500'
                }`}>
                  {group.person_type === 'agent' ? (
                    <User className="h-3 w-3" />
                  ) : (
                    <Users className="h-3 w-3" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                    <span className="text-xs md:text-sm font-medium text-gray-900 dark:text-white">{group.person_name}</span>
                    <span className={`inline-flex px-1.5 py-0.5 text-[10px] md:text-xs font-medium rounded-full ${
                      group.person_type === 'agent'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    }`}>
                      {group.person_type === 'agent' ? 'Agente' : 'Segnalatore'}
                    </span>
                  </div>
                  <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {group.entries.length} {group.entries.length === 1 ? 'voce' : 'voci'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-900 dark:text-white text-sm md:text-base">{formatCurrency(group.total)}</p>
                <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400">Totale</p>
              </div>
            </div>

            {/* Group Entries - Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50 dark:bg-gray-900/30">
                  <tr>
                    <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Servizio
                    </th>
                    <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Importo
                    </th>
                    <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      % / €
                    </th>
                    <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Provvigione
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
                  {group.entries.map((entry, idx) => {
                    const serviceTypeLabel =
                      entry.service_type === 'monthly'
                        ? 'Mensile'
                        : entry.service_type === 'one_time'
                        ? 'Una tantum'
                        : '';

                    return (
                      <tr key={entry.id} className="hover:bg-gray-50/30 dark:hover:bg-gray-900/20 transition-colors">
                        <td className="px-3 py-2">
                          <div>
                            <p className="text-xs font-medium text-gray-900 dark:text-white leading-tight">
                              {entry.service_title || 'Deal generale'}
                            </p>
                            {serviceTypeLabel && (
                              <span className="inline-flex items-center px-1.5 py-0.5 mt-1 rounded text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                {serviceTypeLabel}
                              </span>
                            )}
                            {entry.is_monthly_entry && entry.month_index !== undefined && (
                              <span className="inline-flex items-center px-1.5 py-0.5 mt-1 ml-1 rounded text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                Mese {entry.month_index + 1}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <p className="text-xs font-medium text-gray-900 dark:text-white">{formatCurrency(entry.amount)}</p>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div>
                            <p className="text-xs text-gray-900 dark:text-white font-medium">
                              {entry.commission_type === 'fixed'
                                ? `€${entry.commission_rate.toFixed(2)}`
                                : `${entry.commission_rate}%`
                              }
                            </p>
                            {/* Non mostrare scaling per commissioni fisse - l'importo fisso non viene decurtato */}
                            {entry.scaling_applied && entry.commission_type !== 'fixed' && entry.base_commission_rate && entry.referrer_commission_rate && (
                              <div className="text-xs text-orange-600 dark:text-orange-400 flex items-center justify-end gap-1 mt-0.5">
                                <span className="line-through text-gray-400 dark:text-gray-500">{entry.base_commission_rate}%</span>
                                <span>-{entry.referrer_commission_type === 'fixed'
                                  ? `€${entry.referrer_commission_rate.toFixed(2)}`
                                  : `${entry.referrer_commission_rate}%`
                                }</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <p className="text-xs font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(entry.commission_amount)}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Group Entries - Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200/50 dark:divide-gray-700/50">
              {group.entries.map((entry) => {
                const serviceTypeLabel =
                  entry.service_type === 'monthly'
                    ? 'Mensile'
                    : entry.service_type === 'one_time'
                    ? 'Una tantum'
                    : '';

                return (
                  <div key={entry.id} className="p-3 space-y-2">
                    {/* Service name and badges */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 dark:text-white leading-tight">
                          {entry.service_title || 'Deal generale'}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {serviceTypeLabel && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {serviceTypeLabel}
                            </span>
                          )}
                          {entry.is_monthly_entry && entry.month_index !== undefined && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                              Mese {entry.month_index + 1}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(entry.commission_amount)}
                        </p>
                      </div>
                    </div>

                    {/* Amount and rate row */}
                    <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
                      <span>Importo: {formatCurrency(entry.amount)}</span>
                      <div className="flex items-center gap-1">
                        <span>
                          {entry.commission_type === 'fixed'
                            ? `Fisso: €${entry.commission_rate.toFixed(2)}`
                            : `Rate: ${entry.commission_rate}%`
                          }
                        </span>
                        {/* Non mostrare scaling per commissioni fisse */}
                        {entry.scaling_applied && entry.commission_type !== 'fixed' && entry.base_commission_rate && entry.referrer_commission_rate && (
                          <span className="text-orange-600 dark:text-orange-400">
                            (<span className="line-through">{entry.base_commission_rate}%</span> -{entry.referrer_commission_type === 'fixed'
                              ? `€${entry.referrer_commission_rate.toFixed(2)}`
                              : `${entry.referrer_commission_rate}%`
                            })
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50/50 dark:bg-blue-900/10 backdrop-blur-sm rounded-lg p-2.5 md:p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 md:h-4 md:w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-[10px] md:text-xs text-blue-800 dark:text-blue-300">
            Le provvigioni sono calcolate automaticamente in base alle configurazioni del sistema.
            <span className="hidden md:inline"> Gli importi mostrati sono previsionali e saranno definitivi alla chiusura dell'offerta.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
